import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Dialog,
  DialogContent,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { tokens } from "../../../theme";
import StepHeader from "./StepHeader";

interface Props {
  open: boolean;
  onClose: () => void;
  email: string;
  onSubmit: (password: string, confirmPassword: string) => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

interface PasswordErrors {
  password?: string;
  confirmPassword?: string;
}

export default function CreatePasswordForm({
  open,
  onClose,
  email,
  onSubmit,
  onBack,
  isSubmitting,
}: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<PasswordErrors>({});

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[800],
      borderRadius: "16px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
  };

  const labelSx = {
    fontWeight: 600,
    color: colors.primary[700],
    mb: 0.5,
    fontSize: "0.88rem",
  };

  const validate = (): boolean => {
    const newErrors: PasswordErrors = {};
    if (!password || password.length < 8)
      newErrors.password = "Must be at least 8 characters";
    if (!confirmPassword)
      newErrors.confirmPassword = "Please confirm your password";
    else if (password !== confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        sx: { borderRadius: "16px", overflow: "hidden", width: "400px" },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 10,
          top: 10,
          color: "#fff",
          zIndex: 10,
        }}
      >
        <CloseIcon />
      </IconButton>

      <StepHeader>
        <Typography
          variant="h6"
          fontWeight={600}
          color="#fff"
          textAlign="center"
        >
          Secure your account
        </Typography>
        <Typography
          fontSize={12}
          fontWeight={400}
          color="#fff"
          textAlign="center"
        >
          Create a password to access your dashboard and track your earnings.
        </Typography>
      </StepHeader>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, py: 1 }}>
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Email
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={email}
              InputProps={{ readOnly: true }}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={labelSx}>
              Password
            </Typography>
            <TextField
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password)
                  setErrors((p) => ({ ...p, password: undefined }));
              }}
              error={!!errors.password}
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: errors.password ? "error.main" : colors.primary[600],
                ml: 0.5,
              }}
            >
              {errors.password ?? "Must be at least 8 characters"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" sx={labelSx}>
              Confirm Password
            </Typography>
            <TextField
              fullWidth
              size="small"
              type={showConfirm ? "text" : "password"}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword)
                  setErrors((p) => ({ ...p, confirmPassword: undefined }));
              }}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirm((v) => !v)}
                      edge="end"
                    >
                      {showConfirm ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              variant="contained"
              size="large"
              disabled={isSubmitting}
              onClick={() => {
                if (validate()) onSubmit(password, confirmPassword);
              }}
              sx={{
                backgroundColor: "#724E41",
                px: 6,
                py: 1.3,
                borderRadius: "24px",
                "&:hover": { backgroundColor: "#4E342E" },
                "&.Mui-disabled": { backgroundColor: "#c4a99e", color: "#fff" },
              }}
            >
              Create Account
            </Button>

            <Button
              size="small"
              onClick={onBack}
              disabled={isSubmitting}
              sx={{
                color: colors.primary[600],
                textTransform: "none",
                fontSize: "0.82rem",
                "&:hover": { backgroundColor: "transparent", color: "#724E41" },
              }}
            >
              ← Back
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
